/**
 * The icon set, in one place.
 *
 * Eleven of these lived as private consts inside `SideNav.tsx`, which is why
 * two separate reads of this codebase disagreed about how many icons exist and
 * a third concluded there were none. They are lifted here unchanged — same
 * 24-unit viewBox, same 1.7 stroke, same round caps — and the sidebar imports
 * them back, so nothing about the menu moves.
 *
 * The rest are new, drawn to that same spec because a dashboard whose cards
 * carry a glyph needs one per topic and a set drawn to two specs looks like
 * two sets.
 *
 * `aria-hidden` on every one: an icon here always sits beside its own label,
 * so announcing it repeats the label.
 */

const svg = {
  viewBox: '0 0 24 24', width: 20, height: 20, fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.7,
  strokeLinecap: 'round', strokeLinejoin: 'round',
  'aria-hidden': true, focusable: 'false',
} as const;

// ------------------------------------------------------------ lifted as-is

export const IconToday = () => (
  <svg {...svg}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
  </svg>
);
export const IconInbox = () => (
  <svg {...svg}>
    <path d="M3.5 13.5 6 5.5h12l2.5 8v5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2Z" />
    <path d="M3.5 13.5h4l1.2 2.5h6.6l1.2-2.5h4" />
  </svg>
);
export const IconAcademic = () => (
  <svg {...svg}><path d="M12 4 22 9l-10 5L2 9Z" /><path d="M6 11v5.5c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V11" /></svg>
);
export const IconMoney = () => (
  <svg {...svg}>
    <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
    <circle cx="12" cy="12" r="2.6" />
  </svg>
);
export const IconConfig = () => (
  <svg {...svg}>
    <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
    <circle cx="16" cy="7" r="2.2" /><circle cx="8" cy="17" r="2.2" />
  </svg>
);
export const IconHome = () => (
  <svg {...svg}><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V21h13V9.5" /></svg>
);
export const IconChart = () => (
  <svg {...svg}><path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-7" /></svg>
);
export const IconCard = () => (
  <svg {...svg}><rect x="3" y="5.5" width="18" height="13" rx="2.5" /><path d="M3 10h18" /></svg>
);
export const IconGrid = () => (
  <svg {...svg}>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
  </svg>
);
export const IconUser = () => (
  <svg {...svg}><circle cx="12" cy="8.5" r="3.75" /><path d="M4.5 20a7.5 7.5 0 0 1 15 0" /></svg>
);
export const IconHelp = () => (
  <svg {...svg}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.5a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.4" />
    <path d="M12 16.8h.01" />
  </svg>
);

// --------------------------------------------------------------------- new

/** An "i" in a circle: more about this field. The "?" above asks for help. */
export const IconInfo = () => (
  <svg {...svg}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5" />
    <path d="M12 7.6h.01" />
  </svg>
);

/** An invoice. Amount owed. */
export const IconInvoice = () => (
  <svg {...svg}>
    <path d="M6 3.5h9L19 8v12.5H6Z" /><path d="M14.5 3.5V8H19" />
    <path d="M9 12.5h7M9 16h4.5" />
  </svg>
);
/** A padlock. Test access. */
export const IconLock = () => (
  <svg {...svg}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);
/** An open padlock, for the state the closed one is not. */
export const IconUnlock = () => (
  <svg {...svg}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 7.6-1.7" />
  </svg>
);
/** Two people. A parent creating the account, not the child sitting the test. */
export const IconUsers = () => (
  <svg {...svg}>
    <circle cx="9" cy="8.5" r="3.3" />
    <path d="M3 19.5a6 6 0 0 1 12 0" />
    <path d="M16.4 5.9a3.2 3.2 0 0 1 0 6.2" />
    <path d="M18.5 19.5a6 6 0 0 0-3.2-4.8" />
  </svg>
);
/** An envelope. The email box. */
export const IconMail = () => (
  <svg {...svg}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="m4 7.5 8 5.2 8-5.2" />
  </svg>
);
/** A phone. The mobile box. */
export const IconPhone = () => (
  <svg {...svg}>
    <rect x="6.5" y="3" width="11" height="18" rx="2.5" />
    <path d="M10.5 18h3" />
  </svg>
);
/** A speech bubble. The code that arrives by SMS. */
export const IconMessage = () => (
  <svg {...svg}>
    <path d="M4.5 6.5A2 2 0 0 1 6.5 4.5h11a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9l-4 3.4V6.5Z" />
  </svg>
);
/**
 * An eye, and an eye struck through. Revealing a password, and hiding it.
 *
 * A pair, so the button reads as a switch rather than as two unrelated icons.
 * The struck-through one keeps the whole eye underneath and draws the bar over
 * it: an "off" glyph that redraws the shape is a second picture to recognise,
 * and at 20px the difference between two pictures is invisible while the
 * difference between a bar and no bar is not.
 */
export const IconEye = () => (
  <svg {...svg}>
    <path d="M2.5 12S6 5.75 12 5.75 21.5 12 21.5 12 18 18.25 12 18.25 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3.1" />
  </svg>
);
export const IconEyeOff = () => (
  <svg {...svg}>
    <path d="M2.5 12S6 5.75 12 5.75 21.5 12 21.5 12 18 18.25 12 18.25 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3.1" />
    <path d="M4 20 20 4" />
  </svg>
);
/** A dial. The score just posted. */
export const IconGauge = () => (
  <svg {...svg}>
    <path d="M4 17a8 8 0 1 1 16 0" />
    <path d="M12 17l4-4.5" />
  </svg>
);
/** A trophy. Recognition, and turning up. */
export const IconTrophy = () => (
  <svg {...svg}>
    <path d="M7.5 4h9v4.5a4.5 4.5 0 0 1-9 0Z" />
    <path d="M7.5 5.5h-2a2.5 2.5 0 0 0 2.5 4M16.5 5.5h2a2.5 2.5 0 0 1-2.5 4" />
    <path d="M12 13v3.5M9 20h6M10 16.5h4" />
  </svg>
);
/** A lightbulb. What to do next. */
export const IconIdea = () => (
  <svg {...svg}>
    <path d="M9 16.5a6 6 0 1 1 6 0v1.5H9Z" />
    <path d="M10 21h4" />
  </svg>
);
/** A book. Practice and material. */
export const IconBook = () => (
  <svg {...svg}>
    <path d="M4 5.5A2 2 0 0 1 6 3.5h13v14H6a2 2 0 0 0-2 2Z" />
    <path d="M4 19.5a2 2 0 0 1 2-2h13v3H6a2 2 0 0 1-2-1Z" />
  </svg>
);
/** A rising line. Trend over time. */
export const IconTrend = () => (
  <svg {...svg}>
    <path d="M3.5 16.5 9 11l3.5 3.5L20.5 6.5" />
    <path d="M20.5 11v-4.5H16" />
  </svg>
);

// ------------------------------------------------------------- chrome

/** Points onward. Inside a button, so it inherits the label's colour. */
export const IconArrowRight = () => (
  <svg {...svg} width={16} height={16}><path d="M4 12h15M13.5 6.5 20 12l-6.5 5.5" /></svg>
);
/** Back. The mirror of IconArrowRight, drawn rather than rotated so it keeps
    its own stroke ends and cannot inherit a transform from a parent. */
export const IconArrowLeft = () => (
  <svg {...svg} width={20} height={20}><path d="M20 12H5M10.5 6.5 4 12l6.5 5.5" /></svg>
);
/** The disclosure chevron on a row that leads somewhere. */
export const IconChevronRight = () => (
  <svg {...svg} width={16} height={16}><path d="M9 5.5 15.5 12 9 18.5" /></svg>
);
/** A clock face. Duration, and time spent. */
export const IconClock = () => (
  <svg {...svg}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 1.8" /></svg>
);
/** A tick in a ring. One item of advice, done or to do. */
export const IconCheckCircle = () => (
  <svg {...svg}><circle cx="12" cy="12" r="8.5" /><path d="M8.5 12.2l2.4 2.4 4.6-5" /></svg>
);
/** A bare tick, for a filled selection badge that draws its own disc. */
export const IconCheck = () => (
  <svg {...svg} width={16} height={16}><path d="M5 12.5l4 4 10-11" /></svg>
);
/** A hamburger. Opens the navigation drawer on a phone. */
export const IconMenu = () => (
  <svg {...svg}><path d="M4 7h16M4 12h16M4 17h16" /></svg>
);
/** A cross. Closes it again. */
export const IconClose = () => (
  <svg {...svg}><path d="M6 6l12 12M18 6 6 18" /></svg>
);
/** A crown. The plan somebody is on. */
export const IconCrown = () => (
  <svg {...svg}>
    <path d="M3.5 7.5 7 12l5-6.5 5 6.5 3.5-4.5v9.5H3.5Z" />
    <path d="M3.5 19.5h17" />
  </svg>
);
/** A seedling. The encouragement strip. */
export const IconSprout = () => (
  <svg {...svg}>
    <path d="M12 20.5v-7" />
    <path d="M12 13.5C12 10 9.5 7.5 5.5 7.5c0 3.9 2.6 6 6.5 6Z" />
    <path d="M12.6 13.5c0-3 2.1-5.2 5.9-5.2 0 3.4-2.4 5.2-5.9 5.2Z" />
  </svg>
);

// ---------------------------------------------------------- subjects
//
// A subject name is whatever the paper called it, so no icon can be "the"
// icon for one. These are six neutral study glyphs, handed out by a hash of
// the name (lib/subject-tone.ts) so that Fractions is the same shape and the
// same colour everywhere it appears — which is what makes the row read as a
// subject rather than as decoration.

const IconShapes = () => (
  <svg {...svg}><circle cx="8" cy="8" r="4.2" /><rect x="11.5" y="11.5" width="8.5" height="8.5" rx="2" /></svg>
);
const IconAtom = () => (
  <svg {...svg}>
    <circle cx="12" cy="12" r="2.4" />
    <ellipse cx="12" cy="12" rx="9" ry="4" />
    <ellipse cx="12" cy="12" rx="4" ry="9" />
  </svg>
);
const IconLeaf = () => (
  <svg {...svg}><path d="M5 19c-2-6 2-13 14-14 1 12-6 16-12 14Z" /><path d="M5.5 18.5 13 11" /></svg>
);
const IconHeart = () => (
  <svg {...svg}>
    <path d="M12 20s-7.5-4.4-7.5-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.6c0 5-7.5 9.4-7.5 9.4Z" />
  </svg>
);
const IconSigma = () => (
  <svg {...svg}><path d="M17.5 5.5H6.5l5 6.5-5 6.5h11" /></svg>
);
const IconGlobe = () => (
  <svg {...svg}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" /><ellipse cx="12" cy="12" rx="4" ry="8.5" />
  </svg>
);

/** The six, in the order `subjectTone` indexes them. */
export const SUBJECT_ICONS = [
  IconShapes, IconAtom, IconLeaf, IconHeart, IconSigma, IconGlobe,
] as const;

// ------------------------------------------------- the student console (§6)

export const IconSearch = () => (
  <svg {...svg}>
    <circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 4 4" />
  </svg>
);
/** Sort: the two directions, which is what the control changes. */
export const IconSort = () => (
  <svg {...svg}>
    <path d="M7 4.5v15M7 4.5 4 8M7 4.5 10 8M17 19.5v-15M17 19.5 14 16M17 19.5 20 16" />
  </svg>
);
/** The row menu. Vertical, because a horizontal one reads as a loading state. */
export const IconMore = () => (
  <svg {...svg} strokeWidth="2.6">
    <path d="M12 5.5v.01M12 12v.01M12 18.5v.01" />
  </svg>
);
export const IconFile = () => (
  <svg {...svg}>
    <path d="M13.5 3.5H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" />
    <path d="M13.5 3.5V9H19M8.5 13.5h7M8.5 16.5h4.5" />
  </svg>
);
/** Leaves the console for the student's own view of the product. */
export const IconExternal = () => (
  <svg {...svg}>
    <path d="M14 4.5h5.5V10M19.5 4.5 11 13" />
    <path d="M18 14v4.5a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2H10" />
  </svg>
);
export const IconUserPlus = () => (
  <svg {...svg}>
    <circle cx="9.5" cy="8" r="3.75" />
    <path d="M3 20c0-3.3 2.9-5.5 6.5-5.5 1.2 0 2.3.25 3.2.68" />
    <path d="M17.5 14v6M14.5 17h6" />
  </svg>
);
/** Class: the mortarboard, which is the one glyph nobody reads as anything else. */
export const IconCap = () => (
  <svg {...svg}>
    <path d="M12 4.5 21.5 9 12 13.5 2.5 9z" />
    <path d="M6.5 11v4.75c0 1.5 2.5 2.75 5.5 2.75s5.5-1.25 5.5-2.75V11" />
  </svg>
);
/** Clearing the filters: back to where the screen started. */
export const IconReset = () => (
  <svg {...svg}>
    <path d="M4 10a8.5 8.5 0 1 1 1.2 6" /><path d="M4 4.5V10h5.5" />
  </svg>
);
/** Editing a paper's details: the pencil. */
export const IconEdit = () => (
  <svg {...svg}>
    <path d="M14.5 5.5 18.5 9.5" /><path d="M4 20l1-4.5L16 4.5a2.1 2.1 0 0 1 3 3L8 18.5Z" />
  </svg>
);
export const IconTrash = () => (
  <svg {...svg}>
    <path d="M4.5 7h15M10 4h4M6.5 7l.9 12a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4l.9-12" />
    <path d="M10 11v5.5M14 11v5.5" />
  </svg>
);
/** Materialise: the paper packed into every student's box ahead of the window. */
export const IconBox = () => (
  <svg {...svg}>
    <path d="M12 3 20 7.25v9.5L12 21l-8-4.25v-9.5Z" /><path d="M4 7.25 12 11.5l8-4.25M12 11.5V21" />
  </svg>
);
